import {
  BasicNotificationPayloadSchema,
  type LbNotification,
  type LbNotificationView,
  MeetingStartFailedPayloadSchema,
  MeetingDetectedPayloadSchema,
  MeetingInputDeviceConnectedPayloadSchema,
  MeetingNewEventStartedPayloadSchema,
  MeetingPresenceLostPayloadSchema,
  MeetingSegmentSwitchPayloadSchema,
  MeetingSelectedInputSilentPayloadSchema,
  RoutineReportPayloadSchema,
  UpcomingMeetingPayloadSchema,
} from '@electron/shared/lb-notification.types';
import {
  createContext,
  Fragment,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';

import {
  electronApis,
  type ElectronUnsubscribe,
} from '@/features/electron-api';
import { afterFrames } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import type { FileRoutesByTo } from '@/routeTree.gen';
import type { RouteParams, RouteSearch } from '@/types/routes';

import { LbNotifications } from './lb-notifications.components';

type NotificationState = 'enter' | 'dismiss';

export type DismissReason = 'user' | 'auto';

interface LBNotificationsContextValue {
  state: NotificationState;
  notification: LbNotification | undefined;
  queueCount: number;
  dismiss: (reason?: DismissReason) => Promise<void>;
  clearAll: () => Promise<void>;
  lastDismissReasonRef: MutableRefObject<DismissReason | undefined>;
}

const LBNotificationsContext =
  createContext<LBNotificationsContextValue | null>(null);

export const LBNotificationsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<NotificationState>('enter');
  const [notification, setNotification] = useState<LbNotification>();
  const [queueCount, setQueueCount] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const lastDismissReasonRef = useRef<DismissReason | undefined>(undefined);
  const notificationRef = useRef<LbNotification | undefined>(undefined);
  notificationRef.current = notification;

  // Each new notification starts with no recorded dismiss reason.
  useEffect(() => {
    lastDismissReasonRef.current = undefined;
  }, [notification]);

  useEffect(() => {
    if (isReady) return;

    afterFrames(50, () => {
      setIsReady(true);
    });
  }, [isReady]);

  useEffect(() => {
    let unsubscribe: ElectronUnsubscribe;

    const setupListener = async () => {
      unsubscribe = await electronApis.lbNotifications.listen(
        (notification) => {
          const view = notification.view;
          setState('enter');
          if (
            view !== 'basic' &&
            view !== 'upcoming-meeting' &&
            view !== 'meeting-detected' &&
            view !== 'meeting-presence-lost' &&
            view !== 'meeting-selected-input-silent' &&
            view !== 'meeting-input-device-connected' &&
            view !== 'meeting-new-event-started' &&
            view !== 'meeting-segment-switch' &&
            view !== 'meeting-start-failed' &&
            view !== 'routine-report'
          ) {
            logError(
              new Error(`Unknown notification view: ${view}`),
              'Unknown notification view'
            );
            return;
          }

          setNotification(notification);
        }
      );
      electronApis.lbNotifications.ready();
    };

    setupListener();

    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    let unsubscribe: ElectronUnsubscribe;

    const setupQueueListener = async () => {
      const initialCount = await electronApis.lbNotifications.getQueueCount();
      setQueueCount(initialCount);

      unsubscribe = await electronApis.lbNotifications.onQueueChanged(
        (count) => {
          setQueueCount(count);
        }
      );
    };

    setupQueueListener();

    return () => unsubscribe?.();
  }, []);

  const dismiss = useCallback(async (reason?: DismissReason) => {
    // Captured now: the panel can change before the deferred call runs
    const dismissedId = notificationRef.current?.id;
    lastDismissReasonRef.current = reason;
    setState('dismiss');
    electronApis.window.setIgnoreMouseEvents(true, { forward: true });
    afterFrames(50, () => {
      electronApis.lbNotifications.notified(dismissedId);
    });
  }, []);

  const clearAll = async () => {
    lastDismissReasonRef.current = 'user';
    setState('dismiss');
    electronApis.window.setIgnoreMouseEvents(true, { forward: true });
    afterFrames(50, () => {
      electronApis.lbNotifications.clearAll();
    });
  };

  useEffect(
    () =>
      electronApis.lbNotifications.listenRequestDismissNotification(() =>
        dismiss()
      ),
    [dismiss]
  );

  return (
    <LBNotificationsContext.Provider
      value={{
        state,
        notification,
        queueCount,
        dismiss,
        clearAll,
        lastDismissReasonRef,
      }}
    >
      {isReady && (
        <LbNotifications.Root>
          {/* The frame is keyed by view so a swap under the cursor keeps its
              hover state, while the notification it renders is keyed by id so
              each one starts with a fresh auto-action timer. */}
          <LbNotifications.Frame key={notification?.view}>
            <Fragment key={notification?.id}>{children}</Fragment>
          </LbNotifications.Frame>
        </LbNotifications.Root>
      )}
    </LBNotificationsContext.Provider>
  );
};

/**
 * @internal
 * Base hook to access the LBNotifications context
 * @throws Error if used outside of LBNotificationsProvider
 */
const useLBNotifications = () => {
  const ctx = useContext(LBNotificationsContext);
  if (!ctx) throw new Error('useLBNotifications must be used inside provider');
  return ctx;
};

/**
 * Hook to get the dismiss function for the current notification
 * @returns Function to dismiss the current notification
 *
 * @example
 * ```tsx
 * const dismiss = lbNotificationsApis.useDismiss();
 * <button onClick={dismiss}>Close</button>
 * ```
 */
const useLBNotificationsDismiss = () => {
  const { dismiss } = useLBNotifications();
  return dismiss;
};

/** Read the last dismiss reason on unmount to tell a user close from any other teardown. */
const useLBNotificationsLastDismissReason = () => {
  const { lastDismissReasonRef } = useLBNotifications();
  return lastDismissReasonRef;
};

/**
 * Hook to get the clear all function to dismiss all notifications
 * @returns Function to clear all notifications
 *
 * @example
 * ```tsx
 * const clearAll = lbNotificationsApis.useClearAll();
 * <button onClick={clearAll}>Clear All</button>
 * ```
 */
const useLBNotificationsClearAll = () => {
  const { clearAll } = useLBNotifications();
  return clearAll;
};

/**
 * Hook to get the current notification animation state
 * @returns 'enter' when notification is appearing, 'dismiss' when hiding
 *
 * @example
 * ```tsx
 * const state = lbNotificationsApis.useState();
 * return <AnimatePresence>{state === 'enter' && <Content />}</AnimatePresence>
 * ```
 */
const useLBNotificationsState = () => {
  const { state } = useLBNotifications();
  return state;
};

/**
 * Hook to get the current notification data
 * @returns The current notification object or undefined if none
 *
 * @example
 * ```tsx
 * const notification = lbNotificationsApis.useNotification();
 * if (notification) {
 *   return <div>{notification.payload.message}</div>
 * }
 * ```
 */
const useLBNotification = () => {
  const { notification } = useLBNotifications();
  return notification;
};

/**
 * Hook to get the count of queued notifications
 * @returns The number of notifications waiting in the queue (excluding current)
 *
 * @example
 * ```tsx
 * const queueCount = lbNotificationsApis.useQueueCount();
 * return queueCount > 0 ? <ClearAllButton /> : null;
 * ```
 */
const useLBNotificationsQueueCount = () => {
  const { queueCount } = useLBNotifications();
  return queueCount;
};

const useLBNotificationView = (): LbNotificationView | undefined => {
  const { notification } = useLBNotifications();
  return notification?.view;
};

function useNotificationPayload<T>(
  view: LbNotificationView,
  schema: {
    safeParse: (
      data: unknown
    ) => { success: true; data: T } | { success: false; error: unknown };
  }
): T | null {
  const { notification } = useLBNotifications();
  if (!notification || notification.view !== view) return null;
  const result = schema.safeParse(notification.payload);
  if (!result.success) {
    logError(result.error, `Failed to parse ${view} notification payload`);
    return null;
  }
  return result.data;
}

const useBasicNotificationPayload = () =>
  useNotificationPayload('basic', BasicNotificationPayloadSchema);

const useUpcomingMeetingPayload = () =>
  useNotificationPayload('upcoming-meeting', UpcomingMeetingPayloadSchema);

const useMeetingDetectedPayload = () =>
  useNotificationPayload('meeting-detected', MeetingDetectedPayloadSchema);

const useMeetingPresenceLostPayload = () =>
  useNotificationPayload(
    'meeting-presence-lost',
    MeetingPresenceLostPayloadSchema
  );

const useMeetingSelectedInputSilentPayload = () =>
  useNotificationPayload(
    'meeting-selected-input-silent',
    MeetingSelectedInputSilentPayloadSchema
  );

const useMeetingInputDeviceConnectedPayload = () =>
  useNotificationPayload(
    'meeting-input-device-connected',
    MeetingInputDeviceConnectedPayloadSchema
  );

const useMeetingNewEventStartedPayload = () =>
  useNotificationPayload(
    'meeting-new-event-started',
    MeetingNewEventStartedPayloadSchema
  );

const useMeetingSegmentSwitchPayload = () =>
  useNotificationPayload(
    'meeting-segment-switch',
    MeetingSegmentSwitchPayloadSchema
  );

const useMeetingStartFailedPayload = () =>
  useNotificationPayload(
    'meeting-start-failed',
    MeetingStartFailedPayloadSchema
  );

const useRoutineReportPayload = () =>
  useNotificationPayload('routine-report', RoutineReportPayloadSchema);

/**
 * Navigate the main window to a specific route and focus it
 *
 * Useful for notification actions that need to open a specific page in the main window.
 * This will also show and focus the main window if it's hidden.
 *
 * @param route - The route path to navigate to (type-safe)
 * @param options - Navigation options
 * @param options.params - Path parameters (e.g., chatId for /chats/$chatId)
 * @param options.search - Query/search parameters (type-safe based on route)
 *
 * @example
 * ```tsx
 * // Navigate to a chat with path params
 * lbNotificationsApis.navigateToMainWindow('/chats/$chatId', {
 *   params: { chatId: '123' }
 * });
 *
 * // Navigate with search params
 * lbNotificationsApis.navigateToMainWindow('/meetings', {
 *   search: { filter: 'upcoming' }
 * });
 *
 * // Navigate to meetings list (no params needed)
 * lbNotificationsApis.navigateToMainWindow('/meetings');
 * ```
 */
const navigateToMainWindow = <T extends keyof FileRoutesByTo>(
  route: T,
  options?: {
    params?: RouteParams<T>;
    search?: RouteSearch<T>;
    hash?: string;
  }
) => electronApis.window.mainWindowNavigateTo(route, options);

// eslint-disable-next-line react-refresh/only-export-components
export const lbNotificationsApis = {
  useDismiss: useLBNotificationsDismiss,
  useLastDismissReason: useLBNotificationsLastDismissReason,
  useClearAll: useLBNotificationsClearAll,
  useState: useLBNotificationsState,
  useNotification: useLBNotification,
  useNotificationView: useLBNotificationView,
  useBasicNotificationPayload,
  useUpcomingMeetingPayload,
  useMeetingDetectedPayload,
  useMeetingPresenceLostPayload,
  useMeetingSelectedInputSilentPayload,
  useMeetingInputDeviceConnectedPayload,
  useMeetingNewEventStartedPayload,
  useMeetingSegmentSwitchPayload,
  useMeetingStartFailedPayload,
  useRoutineReportPayload,
  useQueueCount: useLBNotificationsQueueCount,
  navigateToMainWindow,
};
